import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import Sidebar from '../components/Sidebar'
import { useLibraryStore } from '../stores/libraryStore'
import type { Accomplishment, AccomplishmentCreateData, AccomplishmentUpdateData } from '../stores/libraryStore'
import AccomplishmentCard from '../components/library/AccomplishmentCard'
import AccomplishmentDetail from '../components/library/AccomplishmentDetail'
import AccomplishmentForm from '../components/library/AccomplishmentForm'
import type { FormData } from '../components/library/AccomplishmentForm'
import FramingsPanel from '../components/library/FramingsPanel'
import UploadCVModal from '../components/library/UploadCVModal'

type PanelMode = 'none' | 'view' | 'edit' | 'create' | 'framings'

export default function LibraryPage() {
  const {
    accomplishments,
    selectedAccomplishment,
    filters,
    competencyVocabulary,
    contextTagVocabulary,
    loadState,
    error,
    fetchAccomplishments,
    fetchAccomplishment,
    setFilter,
    setIncludeArchived,
    clearFilters,
    createAccomplishment,
    updateAccomplishment,
    updateAndConfirm,
    archiveAccomplishment,
    restoreAccomplishment,
    reviewAccomplishment,
    fetchVocabulary,
    clearSelected,
    clearError,
  } = useLibraryStore()

  const navigate = useNavigate()
  const [panelMode, setPanelMode] = useState<PanelMode>('none')
  const [isReviewConfirm, setIsReviewConfirm] = useState(false)
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [newProofIds, setNewProofIds] = useState<Set<string>>(new Set())
  const [toast, setToast] = useState<string | null>(null)

  type StaleCandidate = { id: string; employer: string; role_title: string; period: string | null; period_start_year: number | null }
  const [staleCandidates, setStaleCandidates] = useState<StaleCandidate[]>([])
  const [staleBannerDismissed, setStaleBannerDismissed] = useState(false)
  const [staleExpanded, setStaleExpanded] = useState(false)
  const [staleEditId, setStaleEditId] = useState<string | null>(null)
  const [staleEditPeriod, setStaleEditPeriod] = useState('')
  const [duplicateCount, setDuplicateCount] = useState(0)

  useEffect(() => {
    fetchAccomplishments()
    fetchVocabulary()
  }, [fetchAccomplishments, fetchVocabulary])

  const fetchStaleDates = useCallback(async () => {
    try {
      const res = await api.get<{ data: { stale_candidates: StaleCandidate[] } }>('/api/v1/library/audit/stale-dates')
      setStaleCandidates(res.data.stale_candidates)
    } catch {
      // Non-blocking: stale-date audit is advisory only
    }
  }, [])

  const fetchDuplicates = useCallback(async () => {
    try {
      const res = await api.get<{ data: { count: number } }>('/api/v1/library/audit/duplicates/count')
      setDuplicateCount(res.data.count)
    } catch {
      // Non-blocking: duplicate audit is advisory only
    }
  }, [])

  useEffect(() => {
    fetchStaleDates()
    fetchDuplicates()
  }, [fetchStaleDates, fetchDuplicates])

  // Re-fetch when non-search filters change (search uses its own debounced path)
  useEffect(() => {
    fetchAccomplishments()
  }, [filters.employer, filters.competency, filters.includeArchived, fetchAccomplishments])

  const openView = useCallback(async (a: Accomplishment) => {
    await fetchAccomplishment(a.id)
    setPanelMode('view')
  }, [fetchAccomplishment])

  const openEdit = useCallback(async (a: Accomplishment) => {
    await fetchAccomplishment(a.id)
    setPanelMode('edit')
  }, [fetchAccomplishment])

  const openFramings = useCallback(async (a: Accomplishment) => {
    await fetchAccomplishment(a.id)
    setPanelMode('framings')
  }, [fetchAccomplishment])

  const closePanel = useCallback(() => {
    setPanelMode('none')
    setIsReviewConfirm(false)
    clearSelected()
  }, [clearSelected])

  const openReviewEdit = useCallback(async (a: Accomplishment) => {
    await fetchAccomplishment(a.id)
    setIsReviewConfirm(true)
    setPanelMode('edit')
  }, [fetchAccomplishment])

  const handleCreate = async (data: FormData) => {
    const createData: AccomplishmentCreateData = {
      employer: data.employer,
      role_title: data.role_title,
      description: data.description,
      period: data.period || undefined,
      authority_notes: data.authority_notes || undefined,
      role_context: data.role_context || undefined,
      competency_tags: data.competency_tags,
      context_tags: data.context_tags,
    }
    await createAccomplishment(createData)
    closePanel()
  }

  const handleUpdate = async (data: FormData) => {
    if (!selectedAccomplishment) return
    const updateData: AccomplishmentUpdateData = {
      employer: data.employer,
      role_title: data.role_title,
      period: data.period || undefined,
      authority_notes: data.authority_notes || undefined,
      role_context: data.role_context || undefined,
      competency_tags: data.competency_tags,
      context_tags: data.context_tags,
    }
    if (data.description) {
      updateData.description = data.description
    }
    if (isReviewConfirm) {
      await updateAndConfirm(selectedAccomplishment.id, updateData)
    } else {
      await updateAccomplishment(selectedAccomplishment.id, updateData)
    }
    closePanel()
  }

  const handleArchive = async (id: string) => {
    await archiveAccomplishment(id)
  }

  const handleReview = async (id: string, action: 'confirm' | 'discard') => {
    await reviewAccomplishment(id, action)
  }

  const handleRestore = async (id: string) => {
    await restoreAccomplishment(id)
  }

  const handleUploadComplete = useCallback(async (newCount: number, updatedCount: number) => {
    setShowUploadModal(false)
    // Capture existing IDs before refresh to identify newly added ones
    const existingIds = new Set(useLibraryStore.getState().accomplishments.map((a) => a.id))
    await fetchAccomplishments()
    const after = useLibraryStore.getState().accomplishments
    const freshIds = new Set(after.filter((a) => !existingIds.has(a.id)).map((a) => a.id))
    setNewProofIds(freshIds)

    const parts: string[] = []
    if (newCount > 0) parts.push(`${newCount} proof${newCount !== 1 ? 's' : ''} added`)
    if (updatedCount > 0) parts.push(`${updatedCount} proof${updatedCount !== 1 ? 's' : ''} updated`)
    setToast(
      parts.length > 0 ? `✓ ${parts.join(' · ')}` : 'No new proofs found in this CV. All content was already in your library.'
    )
    setTimeout(() => setToast(null), 5000)
  }, [fetchAccomplishments])

  // Collect unique employers for filter dropdown
  const employers = Array.from(new Set(accomplishments.map((a) => a.employer))).sort()

  // Collect unique competency tags for filter dropdown
  const allCompetencies = Array.from(
    new Set(accomplishments.flatMap((a) => a.competency_tags))
  ).sort()

  const formatTag = (term: string) =>
    term.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  const needsReviewCount = accomplishments.filter(
    (a) => a.extraction_status === 'needs_review'
  ).length

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 lg:ml-64 flex flex-col min-h-screen">
        <main className="flex-1 px-6 py-6 max-w-5xl w-full">
        {/* Title and actions */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-lg font-semibold text-stone-800">Library</h1>
            <p className="text-xs text-stone-400 mt-0.5">
              {accomplishments.length} proof{accomplishments.length !== 1 ? 's' : ''}
              {needsReviewCount > 0 && (
                <span className="ml-2 text-amber-600">
                  ({needsReviewCount} needs review)
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowUploadModal(true)}
              className="rounded px-3 py-1.5 text-xs text-stone-700 border border-stone-200 hover:bg-stone-50"
            >
              ↑ Upload CV
            </button>
            <button
              onClick={() => navigate('/freeform')}
              className="rounded px-3 py-1.5 text-xs text-stone-700 border border-stone-200 hover:bg-stone-50"
            >
              ⊕ Paste Experience
            </button>
            <button
              onClick={() => setPanelMode('create')}
              className="rounded px-3 py-1.5 text-xs text-stone-700 border border-stone-200 hover:bg-stone-50"
            >
              + Add Proof
            </button>
          </div>
        </div>

        {/* Stale-date audit banner */}
        {staleCandidates.length > 0 && !staleBannerDismissed && (
          <div className="mb-5 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            <div className="flex items-center justify-between">
              <span>
                <strong>{staleCandidates.length} Proof{staleCandidates.length !== 1 ? 's' : ''}</strong> are marked as current but may be outdated.{' '}
                <button
                  onClick={() => setStaleExpanded((v) => !v)}
                  className="underline hover:no-underline focus:outline-none"
                >
                  {staleExpanded ? 'Hide' : 'Review them'}
                </button>
              </span>
              <button
                onClick={() => setStaleBannerDismissed(true)}
                className="ml-4 text-amber-500 hover:text-amber-700 focus:outline-none"
                aria-label="Dismiss"
              >
                ✕
              </button>
            </div>

            {staleExpanded && (
              <ul className="mt-3 space-y-2">
                {staleCandidates.map((sc) => (
                  <li key={sc.id} className="flex items-center gap-3">
                    <span className="flex-1 text-xs">
                      <span className="font-medium">{sc.role_title}</span>
                      {' · '}{sc.employer}
                      {' · '}<span className="text-amber-600">{sc.period ?? 'no period'}</span>
                    </span>
                    {staleEditId === sc.id ? (
                      <span className="flex items-center gap-2">
                        <input
                          value={staleEditPeriod}
                          onChange={(e) => setStaleEditPeriod(e.target.value)}
                          placeholder="e.g. 2019-2023"
                          className="rounded border border-amber-300 bg-white px-2 py-0.5 text-xs text-stone-800 focus:border-amber-500 focus:outline-none w-32"
                        />
                        <button
                          onClick={async () => {
                            await updateAccomplishment(sc.id, { period: staleEditPeriod })
                            setStaleEditId(null)
                            await fetchStaleDates()
                            fetchAccomplishments()
                          }}
                          className="rounded bg-amber-600 px-2 py-0.5 text-xs text-white hover:bg-amber-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setStaleEditId(null)}
                          className="text-xs text-amber-600 hover:underline"
                        >
                          Cancel
                        </button>
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setStaleEditId(sc.id)
                            setStaleEditPeriod(sc.period ?? '')
                          }}
                          className="rounded border border-amber-300 px-2 py-0.5 text-xs hover:bg-amber-100"
                        >
                          Update period
                        </button>
                        <button
                          onClick={async () => {
                            const corrected = prompt('Enter end year (e.g. 2023):', String(new Date().getFullYear()))
                            if (!corrected) return
                            const base = sc.period?.replace(/-?\s*(present|current|now|ongoing)$/i, '').trim() ?? ''
                            await updateAccomplishment(sc.id, { period: base ? `${base}-${corrected}` : corrected })
                            await fetchStaleDates()
                            fetchAccomplishments()
                          }}
                          className="rounded border border-amber-300 px-2 py-0.5 text-xs hover:bg-amber-100"
                        >
                          Dismiss
                        </button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Duplicate audit banner */}
        {duplicateCount > 0 && (
          <div className="mb-4 rounded border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm flex items-center justify-between">
            <span className="text-amber-800">
              <strong>{duplicateCount}</strong> possible duplicate{duplicateCount !== 1 ? 's' : ''} found in your library.
            </span>
            <button
              onClick={() => navigate('/audit/duplicates')}
              className="text-amber-700 underline text-xs hover:text-amber-900 focus:outline-none"
            >
              Review →
            </button>
          </div>
        )}

        {/* Search and filters */}
        <div className="flex items-center gap-3 mb-5 flex-wrap">
          <input
            type="text"
            value={filters.search}
            onChange={(e) => {
              const value = e.target.value
              setFilter('search', value)
              if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current)
              searchDebounceRef.current = setTimeout(() => fetchAccomplishments(), 300)
            }}
            placeholder="Search..."
            className="flex-1 min-w-[200px] rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-800 placeholder:text-stone-400 focus:border-stone-400 focus:outline-none"
          />
          <select
            value={filters.employer}
            onChange={(e) => setFilter('employer', e.target.value)}
            className="rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-700 focus:border-stone-400 focus:outline-none"
          >
            <option value="">All Employers</option>
            {employers.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
          <select
            value={filters.competency}
            onChange={(e) => setFilter('competency', e.target.value)}
            className="rounded border border-stone-200 px-3 py-1.5 text-sm text-stone-700 focus:border-stone-400 focus:outline-none"
          >
            <option value="">All Capabilities</option>
            {allCompetencies.map((c) => (
              <option key={c} value={c}>{formatTag(c)}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-stone-600 select-none cursor-pointer">
            <input
              type="checkbox"
              checked={filters.includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="rounded border-stone-300 text-stone-700 focus:ring-stone-400"
            />
            Show archived
          </label>
          {(filters.search || filters.employer || filters.competency || filters.includeArchived) && (
            <button
              onClick={clearFilters}
              className="text-xs text-stone-500 hover:text-stone-700"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={clearError} className="text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}

        {/* Loading */}
        {loadState === 'loading' && (
          <div className="text-center py-12">
            <p className="text-sm text-stone-400">Loading your library...</p>
          </div>
        )}

        {/* Empty state */}
        {loadState === 'ready' && accomplishments.length === 0 && (
          <div className="text-center py-12">
            <p className="text-sm text-stone-400 mb-3">Your library is empty.</p>
            <button
              onClick={() => setPanelMode('create')}
              className="rounded px-3 py-1.5 text-xs text-white bg-stone-800 hover:bg-stone-700"
            >
              Add your first proof
            </button>
          </div>
        )}

        {/* List */}
        <div className="space-y-2">
          {accomplishments.map((a) => (
            <AccomplishmentCard
              key={a.id}
              accomplishment={a}
              onView={() => openView(a)}
              onEdit={() => openEdit(a)}
              onFramings={() => openFramings(a)}
              onArchive={() => handleArchive(a.id)}
              onReview={(action) => handleReview(a.id, action)}
              onReviewEdit={() => openReviewEdit(a)}
              onRestore={() => handleRestore(a.id)}
              isNew={newProofIds.has(a.id)}
            />
          ))}
        </div>
        </main>

        <footer className="py-4 text-center text-xs text-stone-400 border-t border-stone-100">
          <a href="/privacy" className="hover:text-stone-600 transition-colors">
            Privacy
          </a>
        </footer>
      </div>

      {/* Panels */}
      {panelMode === 'view' && selectedAccomplishment && (
        <AccomplishmentDetail
          accomplishment={selectedAccomplishment}
          onClose={closePanel}
          onEdit={() => setPanelMode('edit')}
          onFramings={() => setPanelMode('framings')}
        />
      )}

      {panelMode === 'edit' && selectedAccomplishment && (
        <AccomplishmentForm
          accomplishment={selectedAccomplishment}
          competencyVocabulary={competencyVocabulary}
          contextTagVocabulary={contextTagVocabulary}
          onSave={handleUpdate}
          onCancel={closePanel}
          isEdit
          submitLabel={isReviewConfirm ? 'Add to Library' : undefined}
        />
      )}

      {panelMode === 'create' && (
        <AccomplishmentForm
          competencyVocabulary={competencyVocabulary}
          contextTagVocabulary={contextTagVocabulary}
          onSave={handleCreate}
          onCancel={closePanel}
        />
      )}

      {panelMode === 'framings' && selectedAccomplishment && (
        <FramingsPanel
          accomplishment={selectedAccomplishment}
          onClose={closePanel}
        />
      )}

      {showUploadModal && (
        <UploadCVModal
          onClose={() => setShowUploadModal(false)}
          onComplete={handleUploadComplete}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-lg bg-stone-800 px-5 py-3 text-sm text-white shadow-lg z-[60]">
          {toast}
        </div>
      )}
    </div>
  )
}
